import { Checkbox, Text, Input, Spinner } from "@fluentui/react-components";
import {
  BranchRegular,
  BuildingRegular,
  VideoRegular,
  CallRegular,
  CalendarClockRegular,
  DismissCircleRegular,
} from "@fluentui/react-icons";

// I18Next
import { useTranslation } from 'react-i18next';

// style
import '../ContractStageMultiSelector.Style.css';

// models
import { ContractStage } from "../../../../../models/Entities/Contracts/ContractStage";
import { StageDraft, StageDraftNumericField, StagePendingOperation } from "../ContractStageMultiselector.Types";

/**
 * Presentational row: it owns no state and dispatches nothing. Both the draft
 * and the save are driven by the multiselector container, so unmounting a row
 * (a collapsed branch group) never loses edits nor stalls the save.
 */
interface Props {
  Stage: ContractStage;
  BranchName: string | undefined;
  CurrencyCode: string;
  ShowInputValues: boolean;
  Draft: StageDraft;
  PendingOperation: StagePendingOperation | undefined;
  onDraftChange: (stageId: number, patch: Partial<StageDraft>) => void;
  // --------
  applyToRemoteExecution: boolean;
  applyToInFaceExecution: boolean;
  applyToPhoneExecution: boolean;
  applyToFailvisitExecution: boolean;
  applyToFollowUpExecution: boolean;
}

/** One execution modality with the two values that belong to it. */
interface ModalityRow {
  key: string;
  label: string;
  icon: JSX.Element;
  tone: 'default' | 'warning';
  enabled: boolean;
  coefficientField: StageDraftNumericField;
  priceField: StageDraftNumericField;
}

const ContractStageMultiselectorItemComponent = (props: Props) => {

  // useTranslation
  const [t] = useTranslation();

  const { Draft, Stage } = props;

  /**
   * A modality's coefficient and price sit on the same line: they are the two
   * halves of one decision, and pairing them removes the mental cross-referencing
   * that separate "coefficients" and "prices" columns forced.
   */
  const allModalities: ModalityRow[] = [
    {
      key: 'inFace',
      label: 'Presencial',
      icon: <BuildingRegular fontSize={16} />,
      tone: 'default',
      enabled: props.applyToInFaceExecution,
      coefficientField: 'settlementCoefficient',
      priceField: 'inPersonPrice',
    },
    {
      key: 'remote',
      label: 'Remoto',
      icon: <VideoRegular fontSize={16} />,
      tone: 'default',
      enabled: props.applyToRemoteExecution,
      coefficientField: 'settlementRemoteCoefficient',
      priceField: 'remotePrice',
    },
    {
      key: 'phone',
      label: 'Telefónico',
      icon: <CallRegular fontSize={16} />,
      tone: 'default',
      enabled: props.applyToPhoneExecution,
      coefficientField: 'settlementPhoneCoefficient',
      priceField: 'phonePrice',
    },
    {
      key: 'followUp',
      label: 'Seguimiento',
      icon: <CalendarClockRegular fontSize={16} />,
      tone: 'default',
      enabled: props.applyToFollowUpExecution,
      coefficientField: 'settlementFollowUpCoefficient',
      priceField: 'followUpPrice',
    },
    {
      // the exception case, toned apart from the four regular modalities
      key: 'failure',
      label: 'Falla de selección',
      icon: <DismissCircleRegular fontSize={16} />,
      tone: 'warning',
      enabled: props.applyToFailvisitExecution && Stage.failSelectionEnabled,
      coefficientField: 'settlementScreeningFailureCoefficient',
      priceField: 'screeningFailurePrice',
    },
  ];

  const modalities = allModalities.filter((modality: ModalityRow) => modality.enabled);

  // The column headers carry the names now, so each input needs its own
  // accessible name. The id is scoped by stage: it repeats on every row otherwise.
  const numberInput = (
    field: StageDraftNumericField,
    ariaLabel: string,
    step: number,
    contentBefore?: JSX.Element
  ) => (
    <Input
      id={Stage.id + '-' + field}
      className='StageNumberInput'
      type='number'
      min={0}
      step={step}
      aria-label={ariaLabel}
      value={Draft[field]}
      contentBefore={contentBefore}
      onFocus={e => e.target.select()}
      onChange={(ev, data) => props.onDraftChange(Stage.id, { [field]: data.value } as Partial<StageDraft>)}
    />
  );

  // Leading tag rather than trailing: it keeps the figures flush right, so a
  // column of prices still lines up digit for digit.
  const currencyTag = <span className='StageCurrencyTag'>{props.CurrencyCode}</span>;

  const rowClassNames = ['StageSelectorContainer'];
  if (Draft.isSelected) rowClassNames.push('Selected');
  if (!props.ShowInputValues) rowClassNames.push('NoPrices');

  return (
    <div className={rowClassNames.join(' ')}>
      <div className='StageSelectorColumn StageSelectorColumnStage'>
        <Checkbox
          className='StageNameCheckbox'
          label={Stage.stageName}
          checked={Draft.isSelected}
          onChange={(ev, checked) => props.onDraftChange(Stage.id, { isSelected: checked.checked as boolean })}
        />
        <div className='stageBadgesRow'>
          <Text className='visitModalityBadge'>
            {Stage.isOnSiteMandatory
              ? <BuildingRegular className='badgeIcon' fontSize={14} />
              : <VideoRegular className='badgeIcon' fontSize={14} />}
            {Stage.isOnSiteMandatory
              ? t('protocolStageMultiSelector.stagesSelected.inPersonVisit')
              : t('protocolStageMultiSelector.stagesSelected.remoteVisit')}
          </Text>
          {props.BranchName && (
            <Text className='branchBadge'>
              <BranchRegular className='branchBadgeIcon' fontSize={14} />
              {props.BranchName}
            </Text>
          )}
          {props.PendingOperation !== undefined && <Spinner size='extra-tiny' />}
        </div>
      </div>

      {Draft.isSelected && (
        <>
          <div className='StageSelectorColumn StageSelectorColumnQuantity'>
            {numberInput('amount', 'Cantidad de ' + Stage.stageName, 1)}
          </div>
          <div className='StageSelectorColumn StageSelectorColumnValues'>
            {modalities.map((modality: ModalityRow) => (
              <div className='StageValueRow' key={modality.key}>
                <span className={'StageValueModality Tone-' + modality.tone}>
                  {modality.icon}
                  {modality.label}
                </span>
                {numberInput(modality.coefficientField, 'Coeficiente ' + modality.label + ' de ' + Stage.stageName, 0.01)}
                {props.ShowInputValues &&
                  numberInput(modality.priceField, 'Precio ' + modality.label + ' de ' + Stage.stageName, 0.01, currencyTag)}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default ContractStageMultiselectorItemComponent;
